import React, { useEffect, useState, useCallback, useMemo } from 'react';
import NavHeader from '../components/NavHeader';
import MicrophoneButton from '../components/MicrophoneButton';
import {
  getProjectVendorMappings,
  createProjectVendorMapping,
  deleteProjectVendorMapping,
  fetchVendors,
  fetchProjects,
  fetchVendorAliases,
  createVendorAlias,
  deleteVendorAlias,
  createMappingKeyword,
  deleteMappingKeyword,
  checkKeywordDuplicate,
  createMappingInstruction,
  updateMappingInstruction,
  deleteMappingInstruction,
  updateProjectVendorMapping,
} from '../api';

function ClassificationMappings() {
  const [mappings, setMappings] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // Add form state
  const [newVendorId, setNewVendorId] = useState('');
  const [newProjectId, setNewProjectId] = useState('');
  const [newGlAccount, setNewGlAccount] = useState('');
  const [newGlCostCenter, setNewGlCostCenter] = useState('');
  const [adding, setAdding] = useState(false);

  // Edit modal state
  const [editMapping, setEditMapping] = useState(null);

  // Vendor aliases
  const [vendorAliases, setVendorAliases] = useState([]);
  const [newAliasText, setNewAliasText] = useState('');

  // Keywords
  const [newKeywordText, setNewKeywordText] = useState('');
  const [keywordDuplicateWarning, setKeywordDuplicateWarning] = useState('');

  // AI Instructions
  const [newInstructionText, setNewInstructionText] = useState('');
  const [newInstructionWeight, setNewInstructionWeight] = useState(10);
  const [addingInstruction, setAddingInstruction] = useState(false);
  const [editingInstructionId, setEditingInstructionId] = useState(null);
  const [editInstructionText, setEditInstructionText] = useState('');
  const [editInstructionWeight, setEditInstructionWeight] = useState(10);

  // GL fields editing
  const [editGlAccount, setEditGlAccount] = useState('');
  const [editGlCostCenter, setEditGlCostCenter] = useState('');

  const showToast = useCallback((message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [mappingsRes, vendorsRes, projectsRes] = await Promise.all([
        getProjectVendorMappings(),
        fetchVendors(),
        fetchProjects(),
      ]);
      const mData = mappingsRes.data;
      setMappings(Array.isArray(mData) ? mData : (mData?.results ?? []));
      const vData = vendorsRes.data;
      setVendors(Array.isArray(vData) ? vData : (vData?.results ?? []));
      const pData = projectsRes.data;
      setProjects(Array.isArray(pData) ? pData : (pData?.results ?? []));
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredMappings = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return mappings;
    return mappings.filter(m =>
      [m.project_name, m.vendor_name, m.gl_account, m.gl_cost_center]
        .some(field => (field || '').toLowerCase().includes(term))
    );
  }, [mappings, searchTerm]);

  const handleAdd = async () => {
    if (!newVendorId || !newProjectId) return;
    setAdding(true);
    try {
      await createProjectVendorMapping(
        newProjectId,
        newVendorId,
        newGlAccount || null,
        newGlCostCenter || null
      );
      showToast('Mapping created successfully', 'success');
      setNewVendorId('');
      setNewProjectId('');
      setNewGlAccount('');
      setNewGlCostCenter('');
      setShowAddModal(false);
      loadData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to create mapping');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (mappingId) => {
    if (!window.confirm('Delete this mapping? This will also remove all its AI instructions.')) return;
    try {
      await deleteProjectVendorMapping(mappingId);
      showToast('Mapping deleted', 'success');
      loadData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete mapping');
    }
  };

  // --- Edit Modal Handlers ---

  const refreshEditMapping = async () => {
    const res = await getProjectVendorMappings();
    const data = res.data;
    const all = Array.isArray(data) ? data : (data?.results ?? []);
    setMappings(all);
    if (editMapping) {
      const updated = all.find(m => m.id === editMapping.id);
      if (updated) setEditMapping(updated);
    }
  };

  const loadVendorAliases = async (vendorId) => {
    try {
      const res = await fetchVendorAliases(vendorId);
      const data = res.data;
      setVendorAliases(Array.isArray(data) ? data : (data?.results ?? []));
    } catch {
      setVendorAliases([]);
    }
  };

  const openEditMapping = async (mapping) => {
    setEditMapping(mapping);
    setEditGlAccount(mapping.gl_account || '');
    setEditGlCostCenter(mapping.gl_cost_center || '');
    setNewAliasText('');
    setNewKeywordText('');
    setKeywordDuplicateWarning('');
    setNewInstructionText('');
    setNewInstructionWeight(10);
    setAddingInstruction(false);
    setEditingInstructionId(null);
    loadVendorAliases(mapping.vendor_id);
  };

  const closeEditMapping = () => {
    setEditMapping(null);
    setVendorAliases([]);
    setNewAliasText('');
    setNewKeywordText('');
    setKeywordDuplicateWarning('');
    setNewInstructionText('');
    setNewInstructionWeight(10);
    setAddingInstruction(false);
    setEditingInstructionId(null);
    setEditInstructionText('');
    setEditInstructionWeight(10);
    setEditGlAccount('');
    setEditGlCostCenter('');
  };

  const handleAddAlias = async () => {
    if (!newAliasText.trim() || !editMapping) return;
    try {
      await createVendorAlias(editMapping.vendor_id, newAliasText.trim());
      setNewAliasText('');
      showToast('Alias added', 'success');
      loadVendorAliases(editMapping.vendor_id);
      refreshEditMapping();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to add alias');
    }
  };

  const handleDeleteAlias = async (aliasId) => {
    if (!editMapping) return;
    try {
      await deleteVendorAlias(editMapping.vendor_id, aliasId);
      showToast('Alias removed', 'success');
      loadVendorAliases(editMapping.vendor_id);
      refreshEditMapping();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete alias');
    }
  };

  const handleAddKeyword = async () => {
    if (!newKeywordText.trim() || !editMapping) return;
    try {
      await createMappingKeyword(editMapping.id, newKeywordText.trim());
      setNewKeywordText('');
      setKeywordDuplicateWarning('');
      showToast('Keyword added', 'success');
      refreshEditMapping();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to add keyword');
    }
  };

  const handleDeleteKeyword = async (keywordId) => {
    if (!editMapping) return;
    try {
      await deleteMappingKeyword(editMapping.id, keywordId);
      showToast('Keyword removed', 'success');
      refreshEditMapping();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete keyword');
    }
  };

  const handleCheckKeywordDuplicate = async (keyword) => {
    if (!keyword.trim() || !editMapping) {
      setKeywordDuplicateWarning('');
      return;
    }
    try {
      const res = await checkKeywordDuplicate(editMapping.id, keyword.trim());
      if (res.data?.duplicate) {
        setKeywordDuplicateWarning(
          `Already used by mapping: ${res.data.existing_mapping_vendor || 'another vendor'} → ${res.data.existing_mapping_project || 'another project'}`
        );
      } else {
        setKeywordDuplicateWarning('');
      }
    } catch {
      setKeywordDuplicateWarning('');
    }
  };

  const handleAddInstruction = async () => {
    if (!newInstructionText.trim() || !editMapping) return;
    setAddingInstruction(true);
    try {
      await createMappingInstruction(editMapping.id, newInstructionText.trim(), newInstructionWeight);
      setNewInstructionText('');
      setNewInstructionWeight(10);
      showToast('Instruction added', 'success');
      refreshEditMapping();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to add instruction');
    } finally {
      setAddingInstruction(false);
    }
  };

  const handleDeleteInstruction = async (instructionId) => {
    if (!editMapping) return;
    try {
      await deleteMappingInstruction(editMapping.id, instructionId);
      showToast('Instruction removed', 'success');
      refreshEditMapping();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete instruction');
    }
  };

  const startEditInstruction = (inst) => {
    setEditingInstructionId(inst.id);
    setEditInstructionText(inst.instruction_text);
    setEditInstructionWeight(inst.weight);
  };

  const handleSaveInstruction = async (instructionId) => {
    if (!editMapping) return;
    try {
      await updateMappingInstruction(editMapping.id, instructionId, {
        instruction_text: editInstructionText,
        weight: editInstructionWeight,
      });
      setEditingInstructionId(null);
      showToast('Instruction updated', 'success');
      refreshEditMapping();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update instruction');
    }
  };

  const handleUpdateGl = async () => {
    if (!editMapping) return;
    try {
      await updateProjectVendorMapping(editMapping.id, {
        gl_account: editGlAccount || null,
        gl_cost_center: editGlCostCenter || null,
      });
      showToast('GL fields updated', 'success');
      refreshEditMapping();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update GL fields');
    }
  };

  return (
    <div className="mappings-page">
      <NavHeader actions={
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>Add Mapping</button>
      } />

      {/* Toast */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Search */}
      <div className="projects-search">
        <input
          type="text"
          placeholder="Search by project, vendor, GL account, GL cost center..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <span className="search-count">
            {filteredMappings.length} of {mappings.length} mappings
          </span>
        )}
      </div>

      {/* Mappings Table */}
      <div className="mappings-table-container">
        {loading ? (
          <div className="loading-spinner">Loading mappings...</div>
        ) : (
          <>
            <table className="mappings-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Vendor</th>
                  <th>GL Account</th>
                  <th>GL Cost Center</th>
                  <th>Confidence Weight</th>
                  <th>Times Used</th>
                  <th>Times Corrected</th>
                  <th>Source</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMappings.map((m) => (
                  <tr key={m.id} className="clickable-row" onClick={() => openEditMapping(m)}>
                    <td>{m.project_name}</td>
                    <td>{m.vendor_name}</td>
                    <td>{m.gl_account || '\u2014'}</td>
                    <td>{m.gl_cost_center || '\u2014'}</td>
                    <td>{(m.confidence_weight * 100).toFixed(0)}%</td>
                    <td>{m.times_used}</td>
                    <td>{m.times_corrected}</td>
                    <td>
                      <span className={`source-badge source-${m.source}`}>{m.source}</span>
                    </td>
                    <td>
                      <button
                        className="btn btn-reject btn-sm"
                        onClick={(e) => { e.stopPropagation(); handleDelete(m.id); }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredMappings.length === 0 && searchTerm && (
              <div className="mappings-empty">No matching mappings.</div>
            )}
            {mappings.length === 0 && !searchTerm && (
              <div className="mappings-empty">No project-vendor mappings yet. Click "Add Mapping" above.</div>
            )}
          </>
        )}
      </div>

      {/* Add Mapping Modal */}
      {showAddModal && (
        <div className="project-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="project-modal" onClick={(e) => e.stopPropagation()}>
            <div className="project-modal-header">
              <h2>Add Mapping</h2>
              <button className="project-modal-close" onClick={() => setShowAddModal(false)}>&times;</button>
            </div>

            <div className="project-modal-body">
              <div className="project-modal-row">
                <label>Project *</label>
                <select value={newProjectId} onChange={(e) => setNewProjectId(e.target.value)}>
                  <option value="">Select Project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div className="project-modal-row">
                <label>Vendor *</label>
                <select value={newVendorId} onChange={(e) => setNewVendorId(e.target.value)}>
                  <option value="">Select Vendor</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>

              <div className="project-modal-row-group" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="project-modal-row">
                  <label>GL Account</label>
                  <input
                    type="text"
                    placeholder="Optional"
                    value={newGlAccount}
                    onChange={(e) => setNewGlAccount(e.target.value)}
                  />
                </div>
                <div className="project-modal-row">
                  <label>GL Cost Center</label>
                  <input
                    type="text"
                    placeholder="Optional"
                    value={newGlCostCenter}
                    onChange={(e) => setNewGlCostCenter(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="project-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAdd}
                disabled={!newVendorId || !newProjectId || adding}
              >
                {adding ? 'Adding...' : 'Add Mapping'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Mapping Modal */}
      {editMapping && (
        <div className="project-modal-overlay" onClick={closeEditMapping}>
          <div className="project-modal" onClick={(e) => e.stopPropagation()}>
            <div className="project-modal-header">
              <h2>{editMapping.vendor_name} &rarr; {editMapping.project_name}</h2>
              <button className="project-modal-close" onClick={closeEditMapping}>&times;</button>
            </div>
            <div className="project-modal-body">
              {/* GL Fields */}
              <div className="project-modal-row-group" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="project-modal-row">
                  <label>GL Account</label>
                  <input
                    type="text"
                    placeholder="Optional"
                    value={editGlAccount}
                    onChange={(e) => setEditGlAccount(e.target.value)}
                  />
                </div>
                <div className="project-modal-row">
                  <label>GL Cost Center</label>
                  <input
                    type="text"
                    placeholder="Optional"
                    value={editGlCostCenter}
                    onChange={(e) => setEditGlCostCenter(e.target.value)}
                  />
                </div>
              </div>

              {/* Vendor Aliases */}
              <div className="aliases-section">
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                  Vendor Aliases
                  <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                    (other names this vendor appears as on invoices)
                  </span>
                </div>
                <div className="keyword-chips">
                  {vendorAliases.map((alias) => (
                    <span key={alias.id} className="keyword-chip">
                      <span className="keyword-chip-text">{alias.alias_name}</span>
                      <button
                        className="keyword-delete-btn"
                        onClick={() => handleDeleteAlias(alias.id)}
                        title="Remove alias"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                  {vendorAliases.length === 0 && (
                    <span style={{ fontSize: '0.85em', color: '#999' }}>No aliases yet</span>
                  )}
                </div>
                <div className="keyword-add-row">
                  <input
                    type="text"
                    className="instruction-text-input"
                    placeholder="Add alias name..."
                    value={newAliasText}
                    onChange={(e) => setNewAliasText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newAliasText.trim()) {
                        handleAddAlias();
                      }
                    }}
                  />
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleAddAlias}
                    disabled={!newAliasText.trim()}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Keywords */}
              <div className="keywords-section">
                <div className="instructions-title">
                  Keywords <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>for deterministic matching</span>
                </div>
                <div className="keyword-chips">
                  {(editMapping.keywords || []).map((kw) => (
                    <span key={kw.id} className="keyword-chip">
                      <span className="keyword-chip-text">{kw.keyword}</span>
                      {kw.times_matched > 0 && (
                        <span className="keyword-matched-count">{kw.times_matched}</span>
                      )}
                      <button
                        className="keyword-delete-btn"
                        onClick={() => handleDeleteKeyword(kw.id)}
                        title="Remove keyword"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                  {(editMapping.keywords || []).length === 0 && (
                    <span style={{ fontSize: '0.85em', color: '#999' }}>No keywords yet</span>
                  )}
                </div>
                <div className="keyword-add-row">
                  <input
                    type="text"
                    className="instruction-text-input"
                    placeholder="Add keyword..."
                    value={newKeywordText}
                    onChange={(e) => setNewKeywordText(e.target.value)}
                    onBlur={(e) => handleCheckKeywordDuplicate(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newKeywordText.trim()) {
                        handleAddKeyword();
                      }
                    }}
                  />
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleAddKeyword}
                    disabled={!newKeywordText.trim()}
                  >
                    Add
                  </button>
                </div>
                {keywordDuplicateWarning && (
                  <div className="keyword-duplicate-warning">
                    {keywordDuplicateWarning}
                  </div>
                )}
              </div>

              {/* AI Instructions */}
              <div className="instructions-section">
                <div className="instructions-title">AI Instructions for {editMapping.vendor_name}</div>
                {(editMapping.instructions || []).map((inst) => (
                  <div key={inst.id} className="instruction-row">
                    {editingInstructionId === inst.id ? (
                      <>
                        <input
                          type="text"
                          className="instruction-text-input"
                          value={editInstructionText}
                          onChange={(e) => setEditInstructionText(e.target.value)}
                        />
                        <MicrophoneButton
                          onTranscribed={(text) => setEditInstructionText((prev) => (prev ? `${prev} ${text}` : text))}
                        />
                        <div className="instruction-weight">
                          <input
                            type="range"
                            min="1"
                            max="10"
                            value={editInstructionWeight}
                            onChange={(e) => setEditInstructionWeight(parseInt(e.target.value))}
                            className="weight-slider"
                          />
                          <span className="weight-value">{editInstructionWeight}</span>
                        </div>
                        <button className="btn btn-primary btn-sm" onClick={() => handleSaveInstruction(inst.id)}>Save</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setEditingInstructionId(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <span className="instruction-text">{inst.instruction_text}</span>
                        <div className="instruction-weight">
                          <span className="weight-label">Weight:</span>
                          <span className="weight-value">{inst.weight}/10</span>
                        </div>
                        <button className="btn btn-secondary btn-sm" onClick={() => startEditInstruction(inst)}>Edit</button>
                        <button className="btn btn-reject btn-sm" onClick={() => handleDeleteInstruction(inst.id)}>Del</button>
                      </>
                    )}
                  </div>
                ))}
                {/* Add instruction form */}
                <div className="instruction-add-row">
                  <input
                    type="text"
                    className="instruction-text-input"
                    placeholder="Enter AI instruction..."
                    value={newInstructionText}
                    onChange={(e) => setNewInstructionText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newInstructionText.trim()) {
                        handleAddInstruction();
                      }
                    }}
                  />
                  <MicrophoneButton
                    onTranscribed={(text) => setNewInstructionText((prev) => (prev ? `${prev} ${text}` : text))}
                  />
                  <div className="instruction-weight">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={newInstructionWeight}
                      onChange={(e) => setNewInstructionWeight(parseInt(e.target.value))}
                      className="weight-slider"
                    />
                    <span className="weight-value">{newInstructionWeight}</span>
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleAddInstruction}
                    disabled={!newInstructionText.trim() || addingInstruction}
                  >
                    {addingInstruction ? '...' : 'Add'}
                  </button>
                </div>
              </div>
            </div>
            <div className="project-modal-footer">
              <button className="btn btn-secondary" onClick={closeEditMapping}>Close</button>
              <button className="btn btn-primary" onClick={handleUpdateGl}>Save GL Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClassificationMappings;
